import { useEffect, useRef, useState } from 'react';
import {
  cloneRequestApprovalSteps,
  workflowRequestComposerFingerprint,
  type DraftSaveConflict,
  type DraftSaveStatus,
} from './workflowDraftModel';
import { WorkflowDraftController, workflowDraftConflict, type WorkflowDraftInput, type WorkflowDraftPort, type WorkflowDraftSuccess } from './workflowDraftController';
import type { WorkflowRequestDraftApprovalStep } from '../model';
import { workflowErrorCode } from '../workflowPageSupport';

export function useWorkflowDraftPersistence({
  input, editing, port, fixtureMode, canCreate, readyToSave, readyToSubmit, onDraftPersisted, onComplete, onApprovalStepsNormalized,
}: {
  input: WorkflowDraftInput;
  editing?: { id: string; draftVersion?: number };
  port: WorkflowDraftPort;
  fixtureMode: boolean;
  canCreate: boolean;
  readyToSave: boolean;
  readyToSubmit: boolean;
  onDraftPersisted: (id: string) => void;
  onComplete: (action: 'draft' | 'submit', result: WorkflowDraftSuccess, snapshot: WorkflowDraftInput) => void;
  onApprovalStepsNormalized: (steps: WorkflowRequestDraftApprovalStep[]) => void;
}) {
  const [activityRequestID, setActivityRequestID] = useState(editing?.id);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [savingAction, setSavingAction] = useState<'draft' | 'submit'>();
  const [draftSaveStatus, setDraftSaveStatus] = useState<DraftSaveStatus>(editing ? 'saved' : 'idle');
  const [draftSaveError, setDraftSaveError] = useState('');
  const [draftSaveConflict, setDraftSaveConflict] = useState<DraftSaveConflict>();
  const [draftSavedAt, setDraftSavedAt] = useState<Date>();
  const [draftSaveRetry, setDraftSaveRetry] = useState(0);
  const [submitRetryPending, setSubmitRetryPending] = useState(false);
  const [checkingSubmission, setCheckingSubmission] = useState(false);
  const commandPending = useRef(false);
  const [error, setError] = useState('');
  const [approvalResolutionError, setApprovalResolutionError] = useState<{ stepLabel?: string; stepOrder?: number; reason?: string }>();
  const latestOnDraftPersisted = useRef(onDraftPersisted);
  latestOnDraftPersisted.current = onDraftPersisted;
  const [controller] = useState(() => new WorkflowDraftController({
    initialDraft: editing,
    fixtureMode,
    port,
    onPersisted(draft) {
      setActivityRequestID(draft.id);
      setActivityRefreshKey((current) => current + 1);
      latestOnDraftPersisted.current(draft.id);
    },
  }));
  const fingerprint = workflowRequestComposerFingerprint(input.templateId, input.title, input.values, input.approvalSteps);
  const currentFingerprint = useRef(fingerprint);
  currentFingerprint.current = fingerprint;
  const baselineFingerprint = useRef(fingerprint);
  const isDirty = fingerprint !== baselineFingerprint.current;

  useEffect(() => {
    if (!canCreate || !isDirty || savingAction || submitRetryPending || draftSaveConflict) return undefined;
    if (!readyToSave) {
      setDraftSaveStatus('idle');
      return undefined;
    }
    const snapshot = controller.snapshot(input);
    setDraftSaveStatus('pending');
    setDraftSaveError('');
    setDraftSaveConflict(undefined);
    const timer = window.setTimeout(() => {
      setDraftSaveStatus('saving');
      void controller.persist(snapshot, port).then((persisted) => {
        const savedLatestChange = currentFingerprint.current === snapshot.fingerprint;
        if (savedLatestChange && persisted.approvalSteps?.length) {
          const normalizedSteps = cloneRequestApprovalSteps(persisted.approvalSteps);
          baselineFingerprint.current = workflowRequestComposerFingerprint(snapshot.templateId, snapshot.title, snapshot.values, normalizedSteps);
          onApprovalStepsNormalized(normalizedSteps);
        } else {
          baselineFingerprint.current = snapshot.fingerprint;
        }
        if (savedLatestChange) {
          setDraftSavedAt(new Date());
          setDraftSaveStatus('saved');
          setDraftSaveError('');
        }
      }, (reason: unknown) => {
        const conflict = workflowDraftConflict(reason);
        setDraftSaveStatus('error');
        setDraftSaveConflict(conflict);
        setDraftSaveError(conflict === 'draft-version'
          ? '다른 창에서 이 초안이 변경되었습니다. 현재 화면의 변경사항은 저장되지 않았습니다.'
          : reason instanceof Error ? reason.message : '초안을 자동 저장하지 못했습니다.');
      });
    }, 1200);
    return () => window.clearTimeout(timer);
    // 입력의 의미가 바뀔 때만 debounce를 재시작하고, 실행 중인 저장은 controller가 직렬화한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreate, controller, fingerprint, draftSaveConflict, draftSaveRetry, isDirty, readyToSave, savingAction, submitRetryPending]);

  const ensureDraftForFile = async () => {
    if (!canCreate) throw new Error('파일을 등록할 권한이 없습니다.');
    if (controller.hasPendingSubmit || commandPending.current) throw new Error('제출 결과를 확인한 뒤 파일을 등록해 주세요.');
    await controller.waitForPersistence();
    if (controller.savedDraftID) return controller.savedDraftID;
    if (!readyToSave) throw new Error('파일을 등록하기 전에 양식과 결재선 설정을 확인해 주세요.');
    const snapshot = controller.snapshot(input);
    setDraftSaveStatus('saving');
    setDraftSaveError('');
    try {
      const persisted = await controller.persist(snapshot, port);
      baselineFingerprint.current = snapshot.fingerprint;
      setDraftSavedAt(new Date());
      setDraftSaveStatus('saved');
      return persisted.id;
    } catch (reason) {
      setDraftSaveStatus('error');
      setDraftSaveError(reason instanceof Error ? reason.message : '파일을 등록할 초안을 만들지 못했습니다.');
      throw reason;
    }
  };

  const submit = async (publish: boolean) => {
    if (!canCreate || commandPending.current || (controller.hasPendingSubmit && !publish) || (publish ? !readyToSubmit : !readyToSave)) return;
    commandPending.current = true;
    const snapshot = controller.snapshot(input);
    setSavingAction(publish ? 'submit' : 'draft');
    setError('');
    setApprovalResolutionError(undefined);
    setDraftSaveError('');
    setDraftSaveConflict(undefined);
    const result = await controller.execute(snapshot, publish, () => {
      baselineFingerprint.current = snapshot.fingerprint;
      setDraftSavedAt(new Date());
      setDraftSaveStatus('saved');
    }, port);
    if (result.ok) {
      setSubmitRetryPending(false);
      onComplete(publish ? 'submit' : 'draft', result, snapshot);
    } else {
      const { reason, conflict, draftPersisted, ambiguousSubmit } = result;
      if (workflowErrorCode(reason) === 'APPROVER_RESOLUTION_FAILED') {
        const details = reason && typeof reason === 'object' && 'details' in reason ? reason.details : undefined;
        if (details && typeof details === 'object') setApprovalResolutionError({
          stepLabel: 'stepLabel' in details && typeof details.stepLabel === 'string' ? details.stepLabel : undefined,
          stepOrder: 'stepOrder' in details && typeof details.stepOrder === 'number' ? details.stepOrder : undefined,
          reason: 'reason' in details && typeof details.reason === 'string' ? details.reason : undefined,
        });
        else setApprovalResolutionError({});
      }
      setSubmitRetryPending(ambiguousSubmit);
      setDraftSaveConflict(conflict);
      if (conflict || !draftPersisted) {
        setDraftSaveStatus('error');
        setDraftSaveError(conflict === 'draft-version'
          ? '다른 창에서 이 초안이 변경되었습니다. 현재 화면의 변경사항은 저장되지 않았습니다.'
          : reason instanceof Error ? reason.message : '요청을 임시저장하지 못했습니다.');
      }
      setError(ambiguousSubmit
        ? '제출 결과를 확인하지 못해 편집을 잠시 잠갔습니다. 같은 제출을 다시 시도하거나 최신 상태를 확인해 주세요.'
        : reason instanceof Error ? reason.message : publish ? '요청을 제출하지 못했습니다.' : '요청을 임시저장하지 못했습니다.');
    }
    commandPending.current = false;
    setSavingAction(undefined);
  };

  const checkSubmission = async () => {
    if (commandPending.current || !controller.hasPendingSubmit) return undefined;
    commandPending.current = true;
    setCheckingSubmission(true);
    try {
      const latest = await controller.checkSubmission(port);
      if (latest) {
        setSubmitRetryPending(false);
        setError('');
        return latest;
      }
      setError('아직 제출 결과가 확정되지 않았습니다. 편집은 잠긴 상태이며 같은 제출을 안전하게 다시 시도할 수 있습니다.');
    } catch {
      setError('최신 상태를 확인하지 못했습니다. 입력 내용은 보존되어 있습니다. 다시 확인하거나 같은 제출을 재시도해 주세요.');
    } finally {
      commandPending.current = false;
      setCheckingSubmission(false);
    }
    return undefined;
  };

  const retryDraftSave = () => {
    if (controller.hasPendingSubmit || commandPending.current) return;
    setDraftSaveError('');
    setDraftSaveConflict(undefined);
    setDraftSaveRetry((current) => current + 1);
  };
  return {
    savedDraftID: controller.savedDraftID, isDirty, activityRequestID, activityRefreshKey,
    savingAction, draftSaveStatus, draftSaveError, draftSaveConflict, draftSavedAt,
    submitRetryPending, checkingSubmission, checkSubmission, error, approvalResolutionError, ensureDraftForFile, submit, retryDraftSave,
  };
}
